/**
 * Gap-based indexing for work item reorder.
 *
 * Items are spaced GAP apart (e.g. 10000, 20000, 30000). On reorder, only
 * the moved item gets a new displayIndex (midpoint between its new neighbors).
 * When gaps collapse below MIN_GAP, the entire group is re-normalized.
 */

export const DISPLAY_INDEX_GAP = 10_000;

/** Minimum gap between two adjacent items before re-normalization is triggered. */
const MIN_GAP = 2;

export interface IndexedItem {
  id: string;
  guid: string;
  type: string;
  displayIndex: number;
}

export interface ReorderResult {
  /** Items whose displayIndex changed — send these to the API. */
  updates: { guid: string; type: string; displayIndex: number }[];
  /** Map of id → new displayIndex for optimistic local state updates. */
  localUpdates: Map<string, number>;
}

/**
 * Returns true when a group's indices are too densely packed to support
 * midpoint insertion — either all identical (legacy unset data) or spanning
 * a range smaller than one GAP (legacy sequential 0, 1, 2, …).
 */
export function needsNormalization(items: IndexedItem[]): boolean {
  if (items.length <= 1) return false;
  const indices = items.map(i => i.displayIndex);
  const range = Math.max(...indices) - Math.min(...indices);
  return range < DISPLAY_INDEX_GAP;
}

/**
 * Assign GAP-spaced indices to every item in the array.
 * First item gets GAP, second gets 2*GAP, etc.
 */
export function normalizeGroup(sortedItems: IndexedItem[]): ReorderResult {
  const updates: ReorderResult['updates'] = [];
  const localUpdates = new Map<string, number>();

  sortedItems.forEach((item, idx) => {
    const newIndex = (idx + 1) * DISPLAY_INDEX_GAP;
    updates.push({ guid: item.guid, type: item.type, displayIndex: newIndex });
    localUpdates.set(item.guid, newIndex);
  });

  return { updates, localUpdates };
}

/**
 * Calculate a displayIndex for an item inserted between two neighbors.
 *
 * @returns The new displayIndex, or `null` when the gap is too small
 *          and the group needs re-normalization instead.
 */
export function calculateMidpoint(
  prevItem: IndexedItem | undefined,
  nextItem: IndexedItem | undefined,
): number | null {
  if (!prevItem && nextItem) {
    // Moving to beginning of group
    const half = Math.round(nextItem.displayIndex / 2);
    return half >= 1 ? half : null;
  }

  if (prevItem && !nextItem) {
    // Moving to end of group
    return prevItem.displayIndex + DISPLAY_INDEX_GAP;
  }

  if (prevItem && nextItem) {
    // Moving between two items
    if (nextItem.displayIndex - prevItem.displayIndex < MIN_GAP) {
      return null; // gap exhaustion
    }
    return Math.round((prevItem.displayIndex + nextItem.displayIndex) / 2);
  }

  // Only item in group — shouldn't normally happen (source === target guard)
  return DISPLAY_INDEX_GAP;
}

/**
 * Compute the full reorder result for a drag-and-drop within a group.
 *
 * @param groupItems  All items in the assignee group, sorted by displayIndex.
 * @param sourceGuid  The guid of the dragged item.
 * @param targetGuid  The guid of the drop-target item (source is inserted before it).
 */
export function computeReorder(
  groupItems: IndexedItem[],
  sourceGuid: string,
  targetGuid: string,
): ReorderResult {
  if (!groupItems.find(item => item.guid === sourceGuid)) {
    return { updates: [], localUpdates: new Map() };
  }

  // When all items share the same displayIndex (legacy data), physically
  // reorder first, then normalize the entire array with fresh GAP spacing.
  if (needsNormalization(groupItems)) {
    const sourceItem = groupItems.find(item => item.guid === sourceGuid)!;
    const withoutSource = groupItems.filter(item => item.guid !== sourceGuid);
    const targetIdx = withoutSource.findIndex(item => item.guid === targetGuid);
    const reordered = [...withoutSource];
    reordered.splice(targetIdx, 0, sourceItem);
    return normalizeGroup(reordered);
  }

  // Items have distinct indices — try midpoint calculation.
  const sourceItem = groupItems.find(item => item.guid === sourceGuid)!;
  const withoutSource = groupItems.filter(item => item.guid !== sourceGuid);
  const targetIdx = withoutSource.findIndex(item => item.guid === targetGuid);

  const prevItem = targetIdx > 0 ? withoutSource[targetIdx - 1] : undefined;
  const nextItem = withoutSource[targetIdx]; // target itself becomes "next"

  const midpoint = calculateMidpoint(prevItem, nextItem);

  if (midpoint === null) {
    // Gap exhaustion — reorder then normalize all items in the group.
    const reordered = [...withoutSource];
    reordered.splice(targetIdx, 0, sourceItem);
    return normalizeGroup(reordered);
  }

  // Happy path: single-item update.
  return {
    updates: [{ guid: sourceItem.guid, type: sourceItem.type, displayIndex: midpoint }],
    localUpdates: new Map([[sourceItem.guid, midpoint]]),
  };
}
